"use client";

import { Loader, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border border-border bg-card p-8 md:p-12">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-4">
          {"REQUEST / RECEIVED"}
        </p>
        <h3 className="font-default font-semibold text-3xl md:text-4xl text-foreground leading-none mb-6">
          {"Quote request sent."}
        </h3>
        <p className="text-foreground/80 text-base md:text-lg mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="border border-border bg-transparent text-foreground hover:bg-secondary hover:text-secondary-foreground h-14 px-6 font-default font-semibold">
          {"Send Another"}
        </Button>
      </div>;
  }
  const fields = [{
    idx: "01",
    name: "name",
    label: "Full Name (Required)",
    type: "text",
    placeholder: "John Smith",
    help: "Your primary contact name"
  }, {
    idx: "02",
    name: "phone",
    label: "Phone Number (Required)",
    type: "tel",
    placeholder: "(863) 555-0123",
    help: "Best number to reach you"
  }, {
    idx: "03",
    name: "email",
    label: "Email Address (Optional)",
    type: "email",
    placeholder: "john@example.com",
    help: ""
  }];
  return <div className="border border-border bg-card p-7 md:p-12">
      <Form onSubmit={handleSubmit} className="space-y-6">
        {fields.map((f, i) => <FormField key={f.name} control={control} name={f.name} data-index={i} render={({
        field
      }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-widest text-foreground/70 mb-2">
                  {`${f.label} / ${f.idx}`}
                </FormLabel>
                <FormControl>
                  <Input type={f.type} placeholder={f.placeholder} className="h-14 border border-border bg-background px-5 text-foreground placeholder:text-foreground/40 font-default text-base focus-visible:ring-0 focus-visible:border-secondary" {...field} />
                </FormControl>
                {f.help && <p className="font-mono text-xs text-foreground/50 mt-1">{f.help}</p>}
                <FormMessage className="font-mono text-xs text-destructive" />
              </FormItem>} />)}

        <FormField control={control} name="notes" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-foreground/70 mb-2">
                {"Notes (Optional) / 04"}
              </FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us anything helpful about your bathroom remodel. Optional." rows={6} className="border border-border bg-background px-5 py-4 text-foreground placeholder:text-foreground/40 font-default text-base focus-visible:ring-0 focus-visible:border-secondary min-h-[160px] resize-none" {...field} />
              </FormControl>
              <p className="font-mono text-xs text-foreground/50 mt-1">
                {"The more detail, the better we can assist you."}
              </p>
              <FormMessage className="font-mono text-xs text-destructive" />
            </FormItem>} />

        {submitError && <div className="border border-destructive bg-destructive/10 p-4">
            <p className="font-mono text-xs uppercase tracking-widest text-destructive">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full h-16 bg-secondary text-secondary-foreground hover:bg-secondary/90 font-default font-semibold text-base uppercase tracking-wider transition-all duration-150">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              {"Sending..."}
            </> : <>
              {"Request a Free Quote"}
              <ArrowRight className="ml-2 size-5" />
            </>}
        </Button>
      </Form>
    </div>;
}
export default function Quote() {
  return <section data-section-id="18719" id="request-your-bathroom-remodel-quote" className="bg-background text-foreground py-24 md:py-44 px-5 md:px-8 border-t border-border">
      <div className="max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-8">BATHROOM REMODEL QUOTES</p>
              <h1 className="font-default font-semibold leading-[0.88] tracking-tight text-foreground mb-8" style={{
              fontSize: "clamp(3rem, 8vw, 6rem)"
            }}>
                <span className="block">Request a Bathroom Remodel Quote in Lakeland, FL</span>
                
                
              </h1>

              <div className="w-[200px] h-[1px] bg-foreground mb-8" />

              <p className="text-foreground/80 text-lg md:text-xl leading-relaxed max-w-[38ch] mb-12"><b>Submit the form</b> to request a free bathroom remodel quote in Lakeland, FL and nearby Polk County areas.</p>

              <div className="space-y-0 border-t border-border">
                <div className="flex justify-between items-center py-5 border-b border-border">
                  <span className="font-mono text-xs uppercase tracking-widest text-foreground/60">
                    {"Service Area"}
                  </span>
                  <span className="font-mono text-xs uppercase tracking-widest text-foreground !h-[20px]">LAKELAND, FL</span>
                </div>
                <div className="flex justify-between items-center py-5 border-b border-border">
                  <span className="font-mono text-xs uppercase tracking-widest text-foreground/60">
                    {"Consultation"}
                  </span>
                  <span className="font-mono text-xs uppercase tracking-widest text-foreground">
                    {"Free Estimates"}
                  </span>
                </div>
                <div className="flex justify-between items-center py-5 border-b border-border">
                  <span className="font-mono text-xs uppercase tracking-widest text-foreground/60">
                    {"Availability"}
                  </span>
                  <span className="font-mono text-xs uppercase tracking-widest text-secondary-foreground">REQUEST A QUOTE</span>
                </div>
                <div className="flex justify-between items-center py-5 border-b border-border">
                  <span className="font-mono text-xs uppercase tracking-widest text-foreground/60">
                    {"Quote Requests"}
                  </span>
                  <span className="font-mono text-xs uppercase tracking-widest text-foreground !h-[20px]"><b>Online form on this page</b></span>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <FormProvider formKey="quote_form_contact" formId="quote_form_contact" sectionName="request-your-bathroom-remodel-quote" defaultValues={{
            name: "",
            email: "",
            phone: "",
            notes: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: false,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            notes: {
              required: false
            }
          }} submitText="Request a Free Quote" successMessage="We have received your request. A local provider will contact you shortly to discuss your bathroom remodel." errorMessage="Submission failed. Please check your details or call us directly.">
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}